import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import styled from 'styled-components';
import apiService from '../services/api';
import { riseIn } from './motion';
import { SearchField, Notice, Loading, EmptyState, Button, Rate, rateTone, pressable, IconChevronLeft, IconChevronRight } from './ui';

const TradingPairContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const TradingPairGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(170px, 1fr));
  gap: 8px;
  ${riseIn(0, 600)}

  > * {
    transition: opacity 200ms ease;
  }

  /* Children, not the grid: the entrance animation holds the grid's own opacity at 1. */
  &[aria-busy='true'] > * {
    opacity: 0.5;
  }

  /* Inside the dock panel the grid scrolls on its own, keeping search and paging in reach */
  ${props => props.$compact && `
    max-height: min(44vh, 400px);
    overflow-y: auto;
    overscroll-behavior: contain;
    padding: 2px;
    margin: -2px;
  `}
`;

const TradingPairCard = styled.button`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 6px;
  min-width: 0;
  padding: 12px 14px;
  text-align: left;
  border-radius: var(--radius-sm);
  border: 1px solid ${props => (props.$selected ? 'var(--accent)' : 'var(--separator)')};
  background: ${props => (props.$selected ? 'var(--accent-tint)' : 'var(--surface)')};
  box-shadow: ${props => (props.$selected ? 'inset 0 0 0 1px var(--accent)' : 'none')};
  cursor: pointer;
  ${pressable}

  &:hover {
    border-color: ${props => (props.$selected ? 'var(--accent)' : 'var(--separator-strong)')};
    background: ${props => (props.$selected ? 'var(--accent-tint)' : 'var(--surface-muted)')};
  }
`;

const PairSymbol = styled.span`
  max-width: 100%;
  font-size: 0.9375rem;
  font-weight: 600;
  letter-spacing: -0.01em;
  color: var(--text);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const PairInfo = styled.span`
  display: flex;
  flex-direction: column;
  gap: 1px;
  font-size: 0.8125rem;

  > span:first-child {
    font-size: 0.75rem;
    color: var(--text-tertiary);
  }
`;

const PaginationContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  padding-top: 4px;
`;

const PageInfo = styled.div`
  color: var(--text-secondary);
  font-size: 0.8125rem;
  font-variant-numeric: tabular-nums;
  text-align: center;
`;

const supportedHintExchanges = new Set(['binance', 'okx', 'kucoin', 'bitget', 'aster', 'hyperliquid', 'bitfinex', 'gate', 'mexc', 'htx', 'bybit', 'cryptocom', 'coinex', 'poloniex', 'xtcom', 'bitmart', 'bingx']);

const defaultTradingLocale = {
  searchPlaceholder: 'Search trading pair, e.g. BTC or USDT',
  searchAria: 'Search trading pair',
  loadingMessage: 'Loading trading pairs…',
  errorTitle: 'Error',
  errorPrefix: 'Loading error:',
  placeholderTitle: 'No trading pairs found',
  placeholderSupportedHint: 'Try changing the query or checking your connection.',
  placeholderUnsupportedHint: 'This exchange is not supported yet.',
  placeholderNoExchangeHint: 'Select an exchange to load trading pairs.',
  paginationPrev: 'Previous',
  paginationNext: 'Next',
  paginationInfo: 'Page {current} of {pages} · {total} pairs',
  rateLabel: 'Funding rate',
  errorHint: 'Unable to load trading pairs',
  noCoinM: '{exchange} has no COIN-M contracts. Switch to USDT-M to see available pairs.',
  spotOnly: '{exchange} is a spot exchange without perpetual futures, so funding rates are not available.',
};

// Raw API pair → the pair object the workspace works with.
export const toTradingPair = (pair) => ({
  symbol: pair.symbol,
  baseAsset: pair.symbol.split(':')[0]?.replace('USDT', ''),
  quoteAsset: pair.symbol.includes(':') ? pair.symbol.split(':')[1] : 'USDT',
  price: 0,
  change24h: pair.lastFundingRate * 100,
  status: 'active'
});

function TradingPairSelector({ selectedExchange, selectedTradingPair, onTradingPairChange, onPairsChange, compact = false, locale = {}, contractType = null }) {
  const [searchTerm, setSearchTerm] = useState('');
  const [tradingPairs, setTradingPairs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState({});
  const searchDebounceRef = useRef(null);
  const tradingLocale = useMemo(
    () => ({
      ...defaultTradingLocale,
      ...locale,
    }),
    [locale]
  );

  const loadTradingPairs = useCallback(async (page = 1, search = '') => {
    if (!selectedExchange) return;
    
    setLoading(true);
    try {
      setError(null);
      let response;
      
      if (selectedExchange.id === 'binance') {
        response = await apiService.getBinanceTradingPairs(search, page, 50, contractType || 'usdt-m');
        setTradingPairs(response.data);
        setPagination(response.pagination);
      } else if (selectedExchange.id === 'okx') {
        response = await apiService.getOKXTradingPairs(search, page, 50, contractType || 'usdt-m');
        setTradingPairs(response.data);
        setPagination(response.pagination);
      } else if (selectedExchange.id === 'kucoin') {
        response = await apiService.getKuCoinTradingPairs(search, page, 50, contractType || 'usdt-m');
        setTradingPairs(response.data);
        setPagination(response.pagination);
      } else if (selectedExchange.id === 'gate') {
        response = await apiService.getGateTradingPairs(search, page, 50, contractType || 'usdt-m');
        setTradingPairs(response.data);
        setPagination(response.pagination);
      } else if (selectedExchange.id === 'mexc') {
        response = await apiService.getMEXCTradingPairs(search, page, 50, contractType || 'usdt-m');
        setTradingPairs(response.data);
        setPagination(response.pagination);
      } else if (selectedExchange.id === 'htx') {
        response = await apiService.getHTXTradingPairs(search, page, 50, contractType || 'usdt-m');
        setTradingPairs(response.data);
        setPagination(response.pagination);
      } else if (selectedExchange.id === 'bybit') {
        response = await apiService.getBybitTradingPairs(search, page, 50, contractType || 'usdt-m');
        setTradingPairs(response.data);
        setPagination(response.pagination);
      } else if (selectedExchange.id === 'bitget') {
        response = await apiService.getBitgetTradingPairs(search, page, 50, contractType || 'usdt-m');
        setTradingPairs(response.data);
        setPagination(response.pagination);
      } else if (selectedExchange.id === 'aster') {
        const asterContractType = contractType || 'usdt-m';
        console.log(`🔍 Aster: завантажуємо торгові пари з contractType=${asterContractType}`);
        response = await apiService.getAsterTradingPairs(search, page, 50, asterContractType);
        console.log(`📊 Aster: отримано ${response.data.length} пар`);
        setTradingPairs(response.data);
        setPagination(response.pagination);
      } else if (selectedExchange.id === 'hyperliquid') {
        const hyperliquidContractType = contractType || 'usdt-m';
        console.log(`🔍 Hyperliquid: завантажуємо торгові пари з contractType=${hyperliquidContractType}`);
        response = await apiService.getHyperliquidTradingPairs(search, page, 50, hyperliquidContractType);
        console.log(`📊 Hyperliquid: отримано ${response.data.length} пар`);
        setTradingPairs(response.data);
        setPagination(response.pagination);
      } else if (selectedExchange.id === 'bitfinex') {
        const bitfinexContractType = contractType || 'usdt-m';
        console.log(`🔍 Bitfinex: завантажуємо торгові пари з contractType=${bitfinexContractType}`);
        response = await apiService.getBitfinexTradingPairs(search, page, 50, bitfinexContractType);
        console.log(`📊 Bitfinex: отримано ${response.data.length} пар`);
        setTradingPairs(response.data);
        setPagination(response.pagination);
      } else if (selectedExchange.id === 'cryptocom') {
        const cryptocomContractType = contractType || 'usdt-m';
        console.log(`🔍 Crypto.com: завантажуємо торгові пари з contractType=${cryptocomContractType}`);
        response = await apiService.getCryptocomTradingPairs(search, page, 50, cryptocomContractType);
        console.log(`📊 Crypto.com: отримано ${response.data.length} пар`);
        setTradingPairs(response.data);
        setPagination(response.pagination);
      } else if (selectedExchange.id === 'coinex') {
        const coinexContractType = contractType || 'usdt-m';
        console.log(`🔍 CoinEx: завантажуємо торгові пари з contractType=${coinexContractType}`);
        response = await apiService.getCoinexTradingPairs(search, page, 50, coinexContractType);
        console.log(`📊 CoinEx: отримано ${response.data.length} пар`);
        setTradingPairs(response.data);
        setPagination(response.pagination);
      } else if (selectedExchange.id === 'poloniex') {
        const poloniexContractType = contractType || 'usdt-m';
        console.log(`🔍 Poloniex: завантажуємо торгові пари з contractType=${poloniexContractType}`);
        response = await apiService.getPoloniexTradingPairs(search, page, 50, poloniexContractType);
        console.log(`📊 Poloniex: отримано ${response.data.length} пар`);
        setTradingPairs(response.data);
        setPagination(response.pagination);
      } else if (selectedExchange.id === 'xtcom') {
        const xtcomContractType = contractType || 'usdt-m';
        console.log(`🔍 XT.COM: завантажуємо торгові пари з contractType=${xtcomContractType}`);
        response = await apiService.getXtcomTradingPairs(search, page, 50, xtcomContractType);
        console.log(`📊 XT.COM: отримано ${response.data.length} пар`);
        setTradingPairs(response.data);
        setPagination(response.pagination);
      } else if (selectedExchange.id === 'bitmart') {
        const bitmartContractType = contractType || 'usdt-m';
        console.log(`🔍 BitMart: завантажуємо торгові пари з contractType=${bitmartContractType}`);
        response = await apiService.getBitmartTradingPairs(search, page, 50, bitmartContractType);
        console.log(`📊 BitMart: отримано ${response.data.length} пар`);
        setTradingPairs(response.data);
        setPagination(response.pagination);
      } else if (selectedExchange.id === 'bingx') {
        const bingxContractType = contractType || 'usdt-m';
        console.log(`🔍 BingX: завантажуємо торгові пари з contractType=${bingxContractType}`);
        response = await apiService.getBingxTradingPairs(search, page, 50, bingxContractType);
        console.log(`📊 BingX: отримано ${response.data.length} пар`);
        setTradingPairs(response.data);
        setPagination(response.pagination);
      } else if (selectedExchange.id === 'bithumb') {
        const bithumbContractType = contractType || 'usdt-m';
        console.log(`🔍 Bithumb: завантажуємо торгові пари з contractType=${bithumbContractType}`);
        response = await apiService.getBithumbTradingPairs(search, page, 50, bithumbContractType);
        console.log(`📊 Bithumb: отримано ${response.data.length} пар`);
        setTradingPairs(response.data);
        setPagination(response.pagination);
      } else if (selectedExchange.id === 'upbit') {
        const upbitContractType = contractType || 'usdt-m';
        console.log(`🔍 Upbit: завантажуємо торгові пари з contractType=${upbitContractType}`);
        response = await apiService.getUpbitTradingPairs(search, page, 50, upbitContractType);
        console.log(`📊 Upbit: отримано ${response.data.length} пар`);
        setTradingPairs(response.data);
        setPagination(response.pagination);
      } else {
        // Для інших бірж поки що порожній список
        setTradingPairs([]);
        setPagination({ page: 1, limit: 50, total: 0, pages: 0 });
      }
    } catch (error) {
      console.error('Trading pair load error:', error);
      setError(`${tradingLocale.errorPrefix} ${error.message}`);
      setTradingPairs([]);
      setPagination({});
    } finally {
      setLoading(false);
    }
  }, [selectedExchange, contractType, tradingLocale]);

  useEffect(() => {
    if (selectedExchange) {
      setCurrentPage(1);
      // Очищаємо дані перед завантаженням нових
      setTradingPairs([]);
      loadTradingPairs(1, '');
    } else {
      setTradingPairs([]);
      setPagination({});
    }
  }, [selectedExchange, contractType, loadTradingPairs]);

  useEffect(() => {
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }

    searchDebounceRef.current = setTimeout(() => {
      setCurrentPage(1);
      loadTradingPairs(1, searchTerm);
    }, 500);

    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
    };
  }, [searchTerm, contractType, loadTradingPairs]);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.pages) {
      setCurrentPage(newPage);
      loadTradingPairs(newPage, searchTerm);
    }
  };


  const handlePairSelect = (pair) => {
    onTradingPairChange(toTradingPair(pair));
  };

  // Share the loaded page with the dock's pair strip (latest callback via ref, no re-subscribe).
  const onPairsChangeRef = useRef(onPairsChange);
  onPairsChangeRef.current = onPairsChange;
  useEffect(() => {
    if (onPairsChangeRef.current) onPairsChangeRef.current({ pairs: tradingPairs, loading });
  }, [tradingPairs, loading]);

  const totalPages = pagination.pages ?? 0;
  const totalPairs = pagination.total ?? 0;
  const pageInfoText = tradingLocale.paginationInfo
    .replace('{current}', currentPage)
    .replace('{pages}', totalPages)
    .replace('{total}', totalPairs);

  const isSupportedPairSource = selectedExchange && supportedHintExchanges.has(selectedExchange.id);
  const placeholderHint = selectedExchange
    ? (isSupportedPairSource
      ? tradingLocale.placeholderSupportedHint
      : tradingLocale.placeholderUnsupportedHint)
    : tradingLocale.placeholderNoExchangeHint;

  const searchField = (
    <SearchField
      id="trading-pair-search"
      placeholder={tradingLocale.searchPlaceholder}
      value={searchTerm}
      onChange={(e) => setSearchTerm(e.target.value)}
      aria-label={tradingLocale.searchAria}
    />
  );

  if (loading && tradingPairs.length === 0) {
    return (
      <TradingPairContainer>
        {searchField}
        <Loading>{tradingLocale.loadingMessage}</Loading>
      </TradingPairContainer>
    );
  }

  return (
    <TradingPairContainer>
      {searchField}

      {error && (
        <Notice tone="error" title={tradingLocale.errorTitle}>
          {error}
        </Notice>
      )}

      {tradingPairs.length > 0 ? (
        <>
          <TradingPairGrid aria-busy={loading} $compact={compact}>
            {tradingPairs.map((pair, index) => {
              const isSelected = selectedTradingPair?.symbol === pair.symbol;
              const ratePercent = (pair.lastFundingRate * 100).toFixed(6);
              return (
                <TradingPairCard
                  key={`${pair.symbol}-${index}`}
                  type="button"
                  aria-pressed={isSelected}
                  $selected={isSelected}
                  onClick={() => handlePairSelect(pair)}
                >
                  <PairSymbol title={pair.symbol}>{pair.symbol}</PairSymbol>
                  <PairInfo>
                    <span>{tradingLocale.rateLabel}</span>
                    <Rate $tone={rateTone(pair.lastFundingRate)}>
                      {pair.lastFundingRate > 0 ? '+' : ''}{ratePercent}%
                    </Rate>
                  </PairInfo>
                </TradingPairCard>
              );
            })}
          </TradingPairGrid>

          {pagination.pages > 1 && (
            <PaginationContainer>
              <Button
                type="button"
                $variant="secondary"
                $size="sm"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage <= 1}
              >
                <IconChevronLeft size={16} strokeWidth={2} />
                {tradingLocale.paginationPrev}
              </Button>

              <PageInfo>{pageInfoText}</PageInfo>

              <Button
                type="button"
                $variant="secondary"
                $size="sm"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage >= pagination.pages}
              >
                {tradingLocale.paginationNext}
                <IconChevronRight size={16} strokeWidth={2} />
              </Button>
            </PaginationContainer>
          )}
        </>
      ) : (
        <EmptyState>
          <h3>{tradingLocale.placeholderTitle}</h3>
          {selectedExchange?.id === 'aster' && contractType === 'coin-m' ? (
            <p>{tradingLocale.noCoinM.replace('{exchange}', 'Aster')}</p>
          ) : selectedExchange?.id === 'hyperliquid' && contractType === 'coin-m' ? (
            <p>{tradingLocale.noCoinM.replace('{exchange}', 'Hyperliquid')}</p>
          ) : selectedExchange?.id === 'upbit' ? (
            <p>{tradingLocale.spotOnly.replace('{exchange}', 'Upbit')}</p>
          ) : selectedExchange?.id === 'bithumb' ? (
            <p>{tradingLocale.spotOnly.replace('{exchange}', 'Bithumb')}</p>
          ) : selectedExchange?.id === 'cryptocom' && contractType === 'coin-m' ? (
            <p>{tradingLocale.noCoinM.replace('{exchange}', 'Crypto.com')}</p>
          ) : (
            <p>{placeholderHint}</p>
          )}
        </EmptyState>
      )}
    </TradingPairContainer>
  );
}

export default TradingPairSelector;